import { supabase } from "./supabase";
import type { Oekt, Rolle } from "./session";

/**
 * Fagfunksjonene, og rollen hver av dem gir.
 *
 * ========  MAN VELGER DET MAN ER, IKKE DET MAN FÅR LOV TIL  ========
 *
 * Ingen skal måtte vite hva `crew` betyr for å komme i gang. Man huker av
 * «A-foto», og rollen følger av det — utledet i basen av en utløser, aldri
 * skrevet herfra. Se migrasjon 20261027141000.
 *
 * Det er derfor denne fila ikke har en eneste `role`-skriving i seg. Det
 * eneste som sendes er `craft_id`. Prøver man å sende en rolle, blir den
 * overskrevet, og det er med vilje: en klient kan byttes ut.
 *
 * ========  NØKLENE KOMMER MED INVITASJON  ========
 *
 * *Omgjort 27.10.2026.* Produksjonsleder, produsent og redaktør kan ikke
 * velges her i det hele tatt — prodleder legger inn adressen din under
 * oppsettet, og raden står klar når du logger inn. Invitasjonen ER
 * avgjørelsen; den er ikke en søknad noen må se.
 *
 * Alle andre velger selv, og det gjelder også teknikerne. Jammen trengs i
 * sekundet hen står på sett: en fotograf som dukker opp på en omdisponert
 * dag skal ikke måtte vekke prodleder for å få satt tidskoden.
 */

export interface Fagfunksjon {
  readonly id: string;
  readonly navn: string;
  readonly gruppe: string;
  readonly rolle: Rolle;
}

/** Rollene som bare kommer med invitasjon. Speiler `tilgang.krever_godkjenning`. */
const MED_NOEKLER: readonly Rolle[] = [
  "prodleder", "produsent", "redaktor", "afoto",
  /* Koordinatoren fra 31.10.2026: hen laster opp dagsplanen, og det
     gaar ut til hele crewet. Samme proeve som de fire over —
     spesialtillatelse er for den som kan endre produksjonen eller det
     som sendes ut av den. Se migrasjon 20261031100100. */
  "koordinator",
];

export function kreverInvitasjon(rolle: Rolle): boolean {
  return MED_NOEKLER.includes(rolle);
}

/**
 * De man kan velge selv.
 *
 * Nøklene er silt bort HER og ikke bare i basen, fordi en valgmulighet som
 * ikke virker er verre enn ingen: den som huker av «Redaktør» og blir crew
 * har fått et svar hen ikke ba om. Basen nekter uansett — utløseren setter
 * rollen, og klienten skriver den ikke.
 */
export function selvvalgte(
  liste: readonly Fagfunksjon[],
): readonly Fagfunksjon[] {
  return liste.filter((f) => !kreverInvitasjon(f.rolle));
}

export const ROLLENAVN: Record<Rolle, string> = {
  regi: "regi",
  vj: "VJ",
  redaktor: "redaktør",
  produsent: "produsent",
  klipper: "klipper",
  prodleder: "prodleder",
  crew: "crew",
  reporter: "reporter",
  tekniker: "tekniker",
  afoto: "A-foto",
  koordinator: "koordinator",
};

export async function hentFagfunksjoner(): Promise<readonly Fagfunksjon[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("craft")
    .select("id, name, group_name, role")
    .order("sort_order", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]),
    navn: String(r["name"]),
    gruppe: String(r["group_name"]),
    rolle: String(r["role"]) as Rolle,
  }));
}

export interface MinFagfunksjon {
  readonly craftId: string | null;
  readonly rolle: Rolle;
  readonly godkjentAt: string | null;
}

export async function hentMin(oekt: Oekt): Promise<MinFagfunksjon | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("production_member")
    .select("craft_id, role, approved_at")
    .eq("id", oekt.member.id)
    .maybeSingle();
  if (error !== null || data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  return {
    craftId: r["craft_id"] == null ? null : String(r["craft_id"]),
    rolle: String(r["role"]) as Rolle,
    godkjentAt: r["approved_at"] == null ? null : String(r["approved_at"]),
  };
}

/**
 * Velger fagfunksjon for seg selv.
 *
 * SENDER BARE `craft_id`. Rollen settes av basen, og at den ikke står her
 * er hele poenget — se kommentaren øverst.
 */
export async function velg(
  oekt: Oekt, craftId: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("production_member")
    .update({ craft_id: craftId })
    .eq("id", oekt.member.id);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

export interface Invitert {
  readonly id: string;
  readonly navn: string;
  /** Tom for den som valgte fagfunksjonen sin selv — da fins ingen invitasjon. */
  readonly epost: string;
  readonly fagfunksjon: string;
  readonly rolle: Rolle;
  /** Fagfunksjoner personen har i TILLEGG. Se migrasjon 20261101110000. */
  readonly ekstra: readonly string[];
  readonly ekstraIder: readonly string[];
  readonly kommetInn: boolean;
  /** Naar invitasjonsposten gikk ut. Null betyr at den IKKE er sendt. */
  readonly sendt: string | null;
  /** Invitert, i motsetning til selvvalgt. Styrer hva raden kan tilby. */
  readonly erInvitert: boolean;
  /** Tatt ut av crewet. Raden staar, men gir ingen tilgang. */
  readonly ute: boolean;
}

/**
 * ALLE i produksjonen, med fagfunksjonene sine.
 *
 * ========  HVORFOR IKKE BARE DE INVITERTE  ========
 *
 * Kortet viste bare rader med `invited_email`, og da kunne prodleder
 * ikke gi en ekstra fagfunksjon til den som hadde valgt sin egen — til
 * regissøren som også koordinerer, for eksempel. Det er nettopp den
 * handlingen kortet skal gjøre mulig. «Tilganger» betyr hvem som har
 * hva, ikke hvem som fikk en e-post.
 */
export async function hentTilganger(oekt: Oekt): Promise<readonly Invitert[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("production_member")
    .select(
      "id, display_name, invited_email, user_id, invite_sent_at, role,"
      + " active_to, extra_craft_ids, craft:craft_id(name, role)",
    )
    .eq("production_id", oekt.production.id)
    .order("display_name", { ascending: true });
  if (error !== null || data === null) return [];

  /* Navnene paa de ekstra fagfunksjonene slaas opp i katalogen vi
     uansett henter. En join til ville gitt det samme en gang til. */
  const katalog = new Map((await hentFagfunksjoner()).map((f) => [f.id, f.navn]));

  /* ADRESSEN BAK HVER RAD.
     `invited_email` er bare adressen raden VENTET paa, og den er null
     for den som valgte fagfunksjonen sin selv — blant andre den som
     opprettet produksjonen. Den virkelige adressen ligger i
     `auth.users`, og `medlemmenes_adresser` henter den for den som
     setter opp. To personer med samme navn er ikke sjeldent paa en
     produksjon, og da er adressen det eneste som skiller dem. */
  const adresser = new Map<string, string>();
  const { data: rader } = await supabase.rpc(
    "medlemmenes_adresser", { p_produksjon: oekt.production.id });
  for (const r of (rader ?? []) as { medlem?: string; epost?: string }[]) {
    if (typeof r.medlem === "string" && typeof r.epost === "string") {
      adresser.set(r.medlem, r.epost);
    }
  }

  return (data as unknown as Record<string, unknown>[]).map((r) => {
    const fag = r["craft"] as { name?: string; role?: string } | null;
    const ekstraIder = Array.isArray(r["extra_craft_ids"])
      ? (r["extra_craft_ids"] as unknown[]).map(String) : [];
    const rolle = String(r["role"] ?? "crew") as Rolle;
    return {
      id: String(r["id"]),
      navn: String(r["display_name"]),
      epost: adresser.get(String(r["id"]))
        ?? (r["invited_email"] == null ? "" : String(r["invited_email"])),
      /* ROLLEN NAAR DET IKKE FINNES EN FAGFUNKSJON.
         `opprett_produksjon` skriver rollen rett paa raden uten
         craft_id — den som rigget produksjonen har ingen fagfunksjon
         aa vise til — og da sto det en tankestrek der det burde ha
         staatt «Produksjonsleder». */
      fagfunksjon: fag?.name ?? ROLLENAVN[rolle] ?? "—",
      rolle,
      ekstra: ekstraIder.map((id) => katalog.get(id) ?? "—"),
      ekstraIder,
      kommetInn: r["user_id"] != null,
      sendt: r["invite_sent_at"] == null ? null : String(r["invite_sent_at"]),
      erInvitert: r["invited_email"] != null,
      /* `active_to` i fortiden er den som er tatt ut. Datoen i seg selv
         sier ikke noe mer enn det — se migrasjon 20261101140000. */
      ute: r["active_to"] != null
        && String(r["active_to"]) < new Date().toISOString().slice(0, 10),
    };
  });
}

/**
 * Gir noen en fagfunksjon I TILLEGG til den hen har.
 *
 * ========  HVORFOR IKKE EN INVITASJON TIL  ========
 *
 * Fordi en person har én rad per produksjon — `production_member.id` er
 * forfatteren i atten fremmednøkler, og to rader ville delt arbeidet
 * hens i to. En invitasjon til en som alt er med nektes derfor av
 * `vern_dobbelt_medlemskap`, og DETTE er veien i stedet.
 *
 * `extra_roles` settes av utløseren, ikke her. Prøver noen uten nøkler
 * å skrive feltet, fryses det til det det var. Se 20261101110000.
 */
export async function leggTilFagfunksjon(
  medlemId: string, craftId: string, naavaerende: readonly string[],
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  if (naavaerende.includes(craftId)) return { ok: true };
  const { error } = await supabase
    .from("production_member")
    .update({ extra_craft_ids: [...naavaerende, craftId] })
    .eq("id", medlemId);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/**
 * Tar noen ut av crewet.
 *
 * ========  TO TILFELLER, OG DE ER IKKE LIKE  ========
 *
 * En invitasjon ingen har krevd slettes — en adresse som er skrevet
 * feil er en skrivefeil, ikke et medlemskap. Et medlem som HAR vaert
 * inne blir staaende og settes historisk, fordi raden er forfatteren
 * i atten fremmednoekler og loggen maa kunne vise hvem som skrev en
 * post lenge etterpaa. Basen avgjoer hvilket av de to det er — se
 * migrasjon 20261101140000.
 */
export async function fjernMedlem(
  medlemId: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.rpc("fjern_medlem", { p_medlem: medlemId });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/** Angrer `fjernMedlem` for en som ble staaende. */
export async function gjeninnsettMedlem(
  medlemId: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase.rpc("gjeninnsett_medlem", { p_medlem: medlemId });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/** Tar en ekstra fagfunksjon bort igjen. Hovedfagfunksjonen roeres ikke. */
export async function fjernFagfunksjon(
  medlemId: string, craftId: string, naavaerende: readonly string[],
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("production_member")
    .update({ extra_craft_ids: naavaerende.filter((i) => i !== craftId) })
    .eq("id", medlemId);
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}

/**
 * Inviterer noen.
 *
 * Raden settes med `approved_at`, fordi invitasjonen ER avgjørelsen — den
 * som inviterer har alt bestemt. Utløseren i basen gir rollen når raden
 * bindes til en konto.
 *
 * ⚠ SENDER INGEN E-POST ENNÅ. Å opprette kontoen krever `service_role`,
 * som aldri kan ligge i nettleseren, og en SMTP-leverandør. Begge deler
 * kommer med innlogging — se åpent spørsmål 16 i `docs/11-risiko.md`.
 * Raden står klar til da, og binder seg selv ved første innlogging.
 */
export async function inviter(
  oekt: Oekt, epost: string, craftId: string, navn: string, forkortelse: string,
): Promise<{ ok: boolean; feil?: string; medlemId?: string }> {
  if (supabase === null) return { ok: true };
  const { data, error } = await supabase.from("production_member").insert({
    production_id: oekt.production.id,
    craft_id: craftId,
    invited_email: epost.trim().toLowerCase(),
    display_name: navn.trim(),
    initials: forkortelse.trim().toUpperCase(),
    approved_at: new Date().toISOString(),
    approved_by: oekt.member.id,
  }).select("id").maybeSingle();
  if (error !== null) return { ok: false, feil: error.message };
  return { ok: true, medlemId: (data as { id?: string } | null)?.id ?? "" };
}

/**
 * Sender invitasjonsposten.
 *
 * ========  HVORFOR DEN KALLES HERFRA OG IKKE AV EN UTLOESER  ========
 *
 * En databasewebhook ville sendt fordi raden ble til, uansett flate.
 * Det er riktigere paa papiret, og feil her: en webhook sender i
 * bakgrunnen, og feiler den, er det ingen som ser det. Prodleder tror
 * invitasjonen er ute, og A-foto dukker ikke opp paa opptaksdag 1.
 *
 * Her returneres feilen til den som trykket, raden staar med
 * `invite_sent_at = null`, og flata viser «ikke sendt» med en knapp.
 *
 * ========  KONTOEN LAGES AV FUNKSJONEN  ========
 *
 * `enable_signup = false`, saa en invitert som ikke har konto KAN ikke
 * logge inn. Edge-funksjonen oppretter den med `service_role` — se
 * `supabase/functions/send-invitasjon/index.ts`.
 */
export async function sendInvitasjon(
  medlemId: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { data, error } = await supabase.functions.invoke("send-invitasjon", {
    body: { medlemId },
  });
  if (error !== null) {
    /* Funksjonens egen feiltekst ligger i svarkroppen, ikke i
       `error.message` — den sier bare «non-2xx status code». Uten dette
       ville «Scaleway svarte 403: …» aldri naadd fram til den som
       trykket. */
    const kropp = await lesFeil(error);
    return { ok: false, feil: kropp ?? error.message };
  }
  const svar = data as { feil?: string } | null;
  if (svar?.feil !== undefined) return { ok: false, feil: svar.feil };
  return { ok: true };
}

async function lesFeil(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: unknown }).context;
  if (ctx instanceof Response) {
    try {
      const j = await ctx.json() as { feil?: string };
      if (typeof j.feil === "string") return j.feil;
    } catch { /* ikke json */ }
  }
  return null;
}

/** Binder invitasjonene som står på adressen din. Kalles etter innlogging. */
export async function krevInvitasjoner(): Promise<number> {
  if (supabase === null) return 0;
  const { data, error } = await supabase.rpc("krev_invitasjoner");
  return error === null && typeof data === "number" ? data : 0;
}
